import {useEffect, useRef, useState} from 'react'

/**
 * Simple hook to determine which section should be sticky based on height comparison
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether the sticky behavior is enabled
 * @param {number} options.debounceMs - Debounce delay in milliseconds
 * @returns {Object} - Refs and className states for the sections
 */
export const useStickyScroll = (options = {}) => {
    const {enabled = true, debounceMs = 150} = options
    const imageRef = useRef(null)
    const descriptionRef = useRef(null)
    const [imageClassName, setImageClassName] = useState('')
    const [descriptionClassName, setDescriptionClassName] = useState('')

    useEffect(() => {
        if (!enabled) {
            setImageClassName('')
            setDescriptionClassName('')
            return
        }

        const calculateStickyBehavior = () => {
            if (!imageRef.current || !descriptionRef.current) return

            setImageClassName('')
            setDescriptionClassName('')

            if (window.innerWidth < 1024) {
                return
            }

            const imageHeight = imageRef.current.offsetHeight
            const descriptionHeight = descriptionRef.current.offsetHeight

            // Apply sticky class to the shorter section
            if (imageHeight > descriptionHeight) {
                setDescriptionClassName('sticky-content')
            } else if (descriptionHeight > imageHeight) {
                setImageClassName('sticky-content')
            }
        }

        // Debounce function
        let timeoutId
        const debouncedCalculate = () => {
            clearTimeout(timeoutId)
            timeoutId = setTimeout(calculateStickyBehavior, debounceMs)
        }

        // Initial calculation
        calculateStickyBehavior()
        window.addEventListener('resize', debouncedCalculate)
        return () => {
            window.removeEventListener('resize', debouncedCalculate)
            clearTimeout(timeoutId)
        }
    }, [enabled, debounceMs])

    return {
        imageRef,
        descriptionRef,
        imageClassName,
        descriptionClassName
    }
}
